import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, Delete, Edit, History, Payments } from '@mui/icons-material';
import api from '../../services/api';
import type { OutworkMovement, OutworkPayment, OutworkRate, OutworkSettlement, OutworkType, OutworkWorker, SaveOutworkWorker } from '../../types/factory';
import { Kpi, KpiStrip, PageHeader, ProductSelect, addDaysIso, errorMessage, fmtMoney, fmtQty, fortnightOf, num, todayIso, usePageView } from './shared';
import type { PageView } from './shared';
import { formatDay } from '../../components/commercial/contact';

const VIEWS: PageView[] = [
  { key: 'armadoras', label: 'Armadoras', description: 'Quiénes arman a domicilio y cuántas bolsas tiene cada una en la calle.' },
  { key: 'dia', label: 'Armado afuera', description: 'Bolsas que salen a armar y las que vuelven armadas, por armadora.' },
  { key: 'liquidacion', label: 'Liquidación de armado', description: 'Lo que se le paga a cada armadora por quincena, por bolsa retirada.' },
  { key: 'precios', label: 'Precio por bolsa de armado', description: 'Cuánto se paga por bolsa armada, por producto y desde qué fecha.' },
];

/** Armado afuera (MO calle): bolsas que salen a armar a domicilio, saldo por armadora y pago por quincena. */
const FactoryOutworkPage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [workers, setWorkers] = useState<OutworkWorker[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadWorkers = useCallback(async () => {
    try {
      setWorkers(await api.getOutworkWorkers(true));
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron cargar las armadoras'));
    }
  }, []);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const active = workers.filter(w => w.isActive);
  const inStreet = active.reduce((a, w) => a + w.packagesInStreet, 0);

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Armado afuera'}
        description={flat && current.description ? current.description : <>Bolsas que salen a armar a domicilio. Saldo en la calle = lo que salió − lo que retiraron armado. Se paga por bolsa retirada, por quincena.</>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <KpiStrip>
        <Kpi label="Armadoras activas" value={active.length} />
        <Kpi label="Bolsas en la calle" value={fmtQty(inStreet)} />
      </KpiStrip>
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Armadoras" />
        <Tab label="Cargar día" />
        <Tab label="Liquidación" />
        <Tab label="Precio por bolsa" />
      </Tabs>)}
      {tab === 0 && <WorkersTab workers={workers} reload={loadWorkers} />}
      {tab === 1 && <DayTab workers={active} onSaved={loadWorkers} />}
      {tab === 2 && <SettlementTab />}
      {tab === 3 && <RatesTab />}
    </Box>
  );
};

// ==================== Armadoras ====================

const WorkersTab: React.FC<{ workers: OutworkWorker[]; reload: () => Promise<void> }> = ({ workers, reload }) => {
  const [form, setForm] = useState<(SaveOutworkWorker & { id?: number }) | null>(null);
  const [history, setHistory] = useState<OutworkWorker | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form) return;
    try {
      if (form.id) await api.updateOutworkWorker(form.id, form);
      else await api.createOutworkWorker(form);
      setForm(null);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };

  const remove = async (w: OutworkWorker) => {
    if (!window.confirm(`¿Borrar a ${w.name}? Si tiene movimientos queda inactiva.`)) return;
    await api.deleteOutworkWorker(w.id);
    reload();
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ name: '', phone: '', address: '', isActive: true, notes: '' })}>Nueva armadora</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell align="right">Salieron</TableCell>
                <TableCell align="right">Retiradas</TableCell>
                <TableCell align="right">En la calle</TableCell>
                <TableCell>Último movimiento</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {workers.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Todavía no hay armadoras.</TableCell></TableRow>
              )}
              {workers.map(w => (
                <TableRow key={w.id} hover sx={{ opacity: w.isActive ? 1 : 0.5 }}>
                  <TableCell>{w.name}{!w.isActive && <Chip size="small" label="inactiva" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{w.phone || ''}</TableCell>
                  <TableCell align="right">{fmtQty(w.packagesOut)}</TableCell>
                  <TableCell align="right">{fmtQty(w.packagesIn)}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={w.packagesInStreet > 0 ? 'warning' : w.packagesInStreet < 0 ? 'error' : 'default'} label={fmtQty(w.packagesInStreet)} />
                  </TableCell>
                  <TableCell>{formatDay(w.lastMovementDate)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" title="Movimientos" onClick={() => setHistory(w)}><History fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setForm({ id: w.id, name: w.name, phone: w.phone, address: w.address, isActive: w.isActive, notes: w.notes })}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(w)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Editar armadora' : 'Nueva armadora'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField size="small" label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus />
              <TextField size="small" label="Teléfono" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <TextField size="small" label="Dirección" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
              <TextField size="small" label="Notas" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} multiline />
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activa" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.name}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {history && <HistoryDialog worker={history} onClose={() => { setHistory(null); reload(); }} />}
    </>
  );
};

const HistoryDialog: React.FC<{ worker: OutworkWorker; onClose: () => void }> = ({ worker, onClose }) => {
  const [rows, setRows] = useState<OutworkMovement[] | null>(null);
  const load = useCallback(() => {
    api.getOutworkMovements({ workerId: worker.id, from: addDaysIso(todayIso(), -120), to: todayIso() }).then(setRows);
  }, [worker.id]);
  useEffect(() => { load(); }, [load]);

  const remove = async (m: OutworkMovement) => {
    if (!window.confirm('¿Borrar este movimiento?')) return;
    await api.deleteOutworkMovement(m.id);
    load();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{worker.name} · últimos 4 meses</DialogTitle>
      <DialogContent>
        {!rows ? <CircularProgress /> : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Fecha</TableCell><TableCell>Movimiento</TableCell><TableCell align="right">Bolsas</TableCell><TableCell>Producto</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Sin movimientos.</TableCell></TableRow>}
              {rows.map(m => (
                <TableRow key={m.id}>
                  <TableCell>{formatDay(m.date)}</TableCell>
                  <TableCell><Chip size="small" color={m.type === 'OUT' ? 'warning' : 'success'} label={m.type === 'OUT' ? 'Salió' : 'Retiró'} /></TableCell>
                  <TableCell align="right">{fmtQty(m.packages)}</TableCell>
                  <TableCell>{m.productName || ''}</TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => remove(m)}><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
};

// ==================== Cargar día ====================

const DayTab: React.FC<{ workers: OutworkWorker[]; onSaved: () => Promise<void> }> = ({ workers, onSaved }) => {
  const [date, setDate] = useState(todayIso());
  const [type, setType] = useState<OutworkType>('OUT');
  const [productId, setProductId] = useState<number | null>(null);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const lines = workers.map(w => ({ workerId: w.id, packages: num(qty[w.id]) })).filter(l => l.packages > 0);
  const total = lines.reduce((a, l) => a + l.packages, 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.createOutworkMovements({ date, type, productId, lines });
      setQty({});
      setMessage({ ok: true, text: `Cargadas ${fmtQty(total)} bolsas (${type === 'OUT' ? 'salieron' : 'retiradas'}) para ${lines.length} armadoras.` });
      onSaved();
    } catch (e) {
      setMessage({ ok: false, text: errorMessage(e, 'No se pudo guardar') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Fecha" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <ToggleButtonGroup exclusive size="small" value={type} onChange={(_, v) => v && setType(v)}>
          <ToggleButton value="OUT">Salió a la calle</ToggleButton>
          <ToggleButton value="IN">Retiró armado</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ minWidth: 260 }}><ProductSelect value={productId} onChange={id => setProductId(id)} label="Producto (opcional)" /></Box>
      </Stack>
      {message && <Alert severity={message.ok ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {workers.length === 0 ? (
        <Alert severity="info">Cargá las armadoras en la pestaña "Armadoras".</Alert>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Armadora</TableCell>
                <TableCell align="right">En la calle hoy</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>Bolsas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workers.map(w => (
                <TableRow key={w.id}>
                  <TableCell>{w.name}</TableCell>
                  <TableCell align="right">{fmtQty(w.packagesInStreet)}</TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={qty[w.id] ?? ''} onChange={e => setQty({ ...qty, [w.id]: e.target.value })}
                      inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={{ width: 120 }} />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2}><b>Total</b></TableCell>
                <TableCell align="right"><b>{fmtQty(total)}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
            <Button variant="contained" onClick={save} disabled={saving || lines.length === 0}>Guardar día</Button>
          </Stack>
        </Card>
      )}
    </>
  );
};

// ==================== Liquidación ====================

const SettlementTab: React.FC = () => {
  const current = fortnightOf(todayIso());
  const [from, setFrom] = useState(current.from);
  const [to, setTo] = useState(current.to);
  const [data, setData] = useState<OutworkSettlement | null>(null);
  const [payments, setPayments] = useState<OutworkPayment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.getOutworkSettlement(from, to),
        api.getOutworkPayments({ from, to: addDaysIso(to, 31) }),
      ]);
      setData(s);
      setPayments(p.filter(x => x.periodFrom.slice(0, 10) <= to && x.periodTo.slice(0, 10) >= from));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'No se pudo calcular la liquidación'));
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir: -1 | 1) => {
    const f = fortnightOf(addDaysIso(dir < 0 ? from : to, dir));
    setFrom(f.from);
    setTo(f.to);
  };

  const pay = async (rows: OutworkSettlement['rows']) => {
    const pending = rows.filter(r => r.pending > 0);
    if (pending.length === 0) return;
    const total = pending.reduce((a, r) => a + r.pending, 0);
    if (!window.confirm(`¿Registrar el pago de ${fmtMoney(total)} a ${pending.length} armadora(s)?`)) return;
    try {
      await api.createOutworkPayments(pending.map(r => ({
        workerId: r.workerId, periodFrom: from, periodTo: to, packages: r.packages, amount: r.pending, paidAt: todayIso(),
      })));
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar el pago'));
    }
  };

  const removePayment = async (p: OutworkPayment) => {
    if (!p.id || !window.confirm('¿Anular este pago?')) return;
    await api.deleteOutworkPayment(p.id);
    load();
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Button size="small" onClick={() => shift(-1)}>← Quincena anterior</Button>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" onClick={() => shift(1)}>Quincena siguiente →</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data ? <CircularProgress /> : (
        <Stack spacing={2}>
          {data.missingRate && (
            <Alert severity="warning">Hay retiros sin precio por bolsa vigente a esa fecha: se liquidaron en $0. Cargá el precio en "Precio por bolsa".</Alert>
          )}
          <KpiStrip>
            <Kpi label="Bolsas retiradas" value={fmtQty(data.totalPackages)} />
            <Kpi label="A pagar" value={fmtMoney(data.totalAmount)} />
            <Kpi label="Pagado" value={fmtMoney(data.totalPaid)} />
            <Kpi label="Pendiente" value={fmtMoney(data.totalAmount - data.totalPaid)} color={data.totalAmount - data.totalPaid > 0 ? '#ed6c02' : undefined} />
          </KpiStrip>
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Armadora</TableCell>
                  <TableCell align="right">Bolsas</TableCell>
                  <TableCell align="right">A pagar</TableCell>
                  <TableCell align="right">Pagado</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay retiros en esta quincena.</TableCell></TableRow>}
                {data.rows.map(r => (
                  <TableRow key={r.workerId}>
                    <TableCell>{r.workerName}</TableCell>
                    <TableCell align="right">{fmtQty(r.packages)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.amount)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.paid)}</TableCell>
                    <TableCell align="right">{r.pending > 0 ? <b>{fmtMoney(r.pending)}</b> : fmtMoney(r.pending)}</TableCell>
                    <TableCell align="right">
                      {r.pending > 0 && <Button size="small" onClick={() => pay([r])}>Pagar</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.rows.some(r => r.pending > 0) && (
              <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
                <Button variant="contained" startIcon={<Payments />} onClick={() => pay(data.rows)}>Pagar todo lo pendiente</Button>
              </Stack>
            )}
          </Card>
          {payments.length > 0 && (
            <Card variant="outlined">
              <Typography fontWeight={700} sx={{ p: 2, pb: 0 }}>Pagos de la quincena</Typography>
              <Table size="small">
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{formatDay(p.paidAt)}</TableCell>
                      <TableCell>{p.workerName}</TableCell>
                      <TableCell align="right">{fmtQty(p.packages)} bolsas</TableCell>
                      <TableCell align="right">{fmtMoney(p.amount)}</TableCell>
                      <TableCell align="right"><IconButton size="small" onClick={() => removePayment(p)}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </Stack>
      )}
    </>
  );
};

// ==================== Precio por bolsa ====================

const RatesTab: React.FC = () => {
  const [rates, setRates] = useState<OutworkRate[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [price, setPrice] = useState('');
  const [from, setFrom] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { api.getOutworkRates().then(setRates); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await api.createOutworkRate({ productId, pricePerPackage: num(price), effectiveFrom: from, notes: null });
      setPrice('');
      setError(null);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar el precio'));
    }
  };

  const remove = async (r: OutworkRate) => {
    if (!window.confirm('¿Borrar este precio? Las liquidaciones se recalculan con el anterior.')) return;
    await api.deleteOutworkRate(r.id);
    load();
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cada retiro se paga con el precio vigente a su fecha. Si el producto tiene precio propio se usa ese; si no, el general.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ minWidth: 260 }}><ProductSelect value={productId} onChange={id => setProductId(id)} label="Producto (vacío = general)" /></Box>
          <TextField size="small" type="number" label="Precio por bolsa $" value={price} onChange={e => setPrice(e.target.value)} />
          <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={add} disabled={num(price) <= 0}>Agregar</Button>
        </Stack>
      </Card>
      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Desde</TableCell><TableCell>Producto</TableCell><TableCell align="right">Precio por bolsa</TableCell><TableCell /></TableRow>
          </TableHead>
          <TableBody>
            {rates.length === 0 && <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary' }}>Sin precios cargados.</TableCell></TableRow>}
            {rates.map(r => (
              <TableRow key={r.id}>
                <TableCell>{formatDay(r.effectiveFrom)}</TableCell>
                <TableCell>{r.productName || 'General'}</TableCell>
                <TableCell align="right">{fmtMoney(r.pricePerPackage)}</TableCell>
                <TableCell align="right"><IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};

export default FactoryOutworkPage;
